import type { ESACacheMessage } from '../cache/MessageCache';
import type { OpenCommitment, OpenCommitmentOption } from '../types';

/** Badge letters assigned by position when DESO does not supply an option key. */
const OPTION_LETTERS = 'ABCDEFGH';

/** The letter shown for an option: the authored key wins, else A/B/C by order. */
export function optionLetter(option: OpenCommitmentOption, index: number): string {
  return option.key || OPTION_LETTERS[index] || String(index + 1);
}

export type SettledOpenCommitmentMatch =
  | { kind: 'option'; index: number; option: OpenCommitmentOption }
  | { kind: 'free_text'; text: string };

/**
 * Classify a settled reply as an offered option or free-text chat.
 * Matches exact label/id and single-letter keys (A–H) per card footer hint.
 */
export function matchSettledOpenCommitmentAnswer(
  answer: string,
  options: OpenCommitmentOption[],
): SettledOpenCommitmentMatch {
  const trimmed = answer.trim();
  if (!trimmed) {
    return { kind: 'free_text', text: answer };
  }

  const byLabelOrId = options.findIndex(
    o => o.label.toLowerCase() === trimmed.toLowerCase() || o.id === trimmed,
  );
  if (byLabelOrId >= 0) {
    return { kind: 'option', index: byLabelOrId, option: options[byLabelOrId] };
  }

  if (trimmed.length === 1) {
    const letter = trimmed.toUpperCase();
    const byKey = options.findIndex((o, i) => optionLetter(o, i).toUpperCase() === letter);
    if (byKey >= 0) {
      return { kind: 'option', index: byKey, option: options[byKey] };
    }
  }

  return { kind: 'free_text', text: trimmed };
}

/**
 * Latest agent turn with an open ask that the user has not replied to yet.
 */
export function findLiveOpenCommitment(messages: ESACacheMessage[]): OpenCommitment | null {
  const settlements = buildOpenCommitmentSettlements(messages);
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.initiator === 'user') {
      continue;
    }
    const commitment = readOpenCommitment(message);
    if (!commitment?.options?.length) {
      continue;
    }
    if (settlements.has(message.id)) {
      continue;
    }
    return commitment;
  }
  return null;
}

/** Map composer/chip input to the option label DESO matches (same as row click). */
export function wireTextForOpenCommitmentAnswer(
  text: string,
  commitment: OpenCommitment,
): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return trimmed;
  }
  const options = commitment.options ?? [];
  if (options.length === 0) {
    return trimmed;
  }
  const match = matchSettledOpenCommitmentAnswer(trimmed, options);
  if (match.kind === 'option') {
    return match.option.label;
  }
  return trimmed;
}

/**
 * When a live open_commitment owns the thread, rewrite letter keys and exact
 * label/id picks to the option label before converse (DESO has no A/B/C wire).
 */
export function resolveComposerTextForOpenCommitment(
  text: string,
  messages: ESACacheMessage[],
): string {
  const commitment = findLiveOpenCommitment(messages);
  if (!commitment) {
    return text.trim();
  }
  return wireTextForOpenCommitmentAnswer(text, commitment);
}

function normalizeOption(raw: unknown): OpenCommitmentOption | null {
  if (typeof raw === 'string') {
    const label = raw.trim();
    return label ? { label } : null;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const id = typeof obj.id === 'string' ? obj.id.trim() : '';
  const rawLabel = typeof obj.label === 'string' ? obj.label.trim() : '';
  const rawValue = typeof obj.value === 'string' ? obj.value.trim() : '';
  const label = rawLabel || rawValue || id;
  if (!label) {
    return null;
  }
  const hintSource = [obj.hint, obj.sub, obj.description].find(
    (v): v is string => typeof v === 'string' && v.trim().length > 0,
  );
  const key = typeof obj.key === 'string' ? obj.key.trim() : '';
  return {
    label,
    ...(id ? { id } : {}),
    ...(hintSource ? { hint: hintSource.trim() } : {}),
    ...(key ? { key } : {}),
  };
}

function positiveInt(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 ? raw : undefined;
}

/**
 * Parse open_commitment from message content or converse metadata.
 * Options arrive as bare strings or `{id, label}` objects depending on the ask
 * kind, so both are normalized to `{label, id?, hint?, key?}`.
 *
 * De-duplication keys on the id when there is one: DESO can legitimately offer
 * two distinct ids under the same label (a label collision its clarify-label
 * enricher declined to qualify), and dropping one would make that option
 * unreachable. Identical ids, or repeats of an id-less label, still collapse.
 */
export function parseOpenCommitment(raw: unknown): OpenCommitment | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const kind = typeof obj.kind === 'string' ? obj.kind.trim() : '';
  const question = typeof obj.question === 'string' ? obj.question : '';
  if (!kind && !question && !Array.isArray(obj.options)) {
    return null;
  }

  let options: OpenCommitmentOption[] | undefined;
  if (Array.isArray(obj.options)) {
    const seen = new Set<string>();
    options = obj.options.reduce<OpenCommitmentOption[]>((acc, entry) => {
      const option = normalizeOption(entry);
      if (!option) {
        return acc;
      }
      const identity = option.id ?? option.label;
      if (!seen.has(identity)) {
        seen.add(identity);
        acc.push(option);
      }
      return acc;
    }, []);
  }

  const frame_id = typeof obj.frame_id === 'string' ? obj.frame_id : undefined;
  const index = positiveInt(obj.index);
  const total = positiveInt(obj.total);
  const free_hint = typeof obj.free_hint === 'string' && obj.free_hint.trim()
    ? obj.free_hint.trim()
    : undefined;
  const allow_free = typeof obj.allow_free === 'boolean' ? obj.allow_free : undefined;

  return {
    kind: kind || 'unknown',
    question,
    ...(options ? { options } : {}),
    ...(frame_id ? { frame_id } : {}),
    ...(index !== undefined ? { index } : {}),
    ...(total !== undefined ? { total } : {}),
    ...(free_hint ? { free_hint } : {}),
    ...(allow_free !== undefined ? { allow_free } : {}),
  };
}

/** Prefer the commitment on message content; fall back to the converse metadata bag. */
export function readOpenCommitment(message: ESACacheMessage): OpenCommitment | null {
  return (
    parseOpenCommitment(message.message.open_commitment) ??
    parseOpenCommitment(message.metadata?.open_commitment) ??
    null
  );
}

function userText(message: ESACacheMessage): string {
  const raw = message.message as unknown;
  if (typeof raw === 'string') {
    return raw.trim();
  }
  return typeof message.message.text === 'string' ? message.message.text.trim() : '';
}

/**
 * Map of message id → the reply that answered that message's open ask.
 *
 * DESO repeats the top-of-stack ask on every turn it stays open, so an ask is
 * spent as soon as the user has replied past it. Deriving that from the thread
 * (rather than tracking clicks) keeps the settled state correct after a reload
 * and stops an older turn from being re-answered into a forked flow.
 */
export function buildOpenCommitmentSettlements(
  messages: ESACacheMessage[],
): Map<string, string> {
  const settlements = new Map<string, string>();
  messages.forEach((message, i) => {
    if (message.initiator === 'user' || !readOpenCommitment(message)) {
      return;
    }
    const reply = messages.slice(i + 1).find(m => m.initiator === 'user');
    if (reply) {
      settlements.set(message.id, userText(reply) || 'Answered');
    }
  });
  return settlements;
}
